import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getProfilePlaceholder } from '../../utils/placeholders';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import {
  Clock,
  CheckCircle,
  XCircle,
  DollarSign,
  ShieldCheck,
  X,
  CheckSquare,
  Square
} from 'lucide-react';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

const Payments = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [sortBy, setSortBy] = useState('newest');
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [transactionSearch, setTransactionSearch] = useState('');
  const [transactionDateRange, setTransactionDateRange] = useState('all');
  const [allPayments, setAllPayments] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([]);
  const [showBulkOTPModal, setShowBulkOTPModal] = useState(false);
  const [bulkOtpData, setBulkOtpData] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, completed: 0, failed: 0 });

  useEffect(() => {
    fetchPayments();
  }, [currentUser, filter, sortBy]); // Also refresh when filter or sort changes

  useEffect(() => {
    if (currentUser) {
      fetchAllPayments();
    }
  }, [currentUser]);

  // Fetch campaigns that have ended and their associated influencers
  // Filter payments based on selected filter
  const getFilteredPayments = () => {
    if (filter === 'all') {
      return payments;
    }
    return payments.filter(payment => payment.status === filter);
  };
  
  // Handle viewing payment details
  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };
  
  // Close the detail modal
  const handleCloseModal = () => {
    setSelectedPayment(null);
    setShowModal(false);
  };
  
  // Initialize OTP functions
  const functions = getFunctions();
  const sendPaymentOTP = httpsCallable(functions, 'sendPaymentOTP');
  const verifyPaymentOTP = httpsCallable(functions, 'verifyPaymentOTP');

  // Timer effect for OTP countdown
  useEffect(() => {
    let interval = null;
    if (timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [timeLeft]);

  // Process payment for an influencer - now triggers OTP flow
  const handleProcessPayment = async (paymentId) => {
    try {
      setProcessingPayment(true);
      
      // Get the payment data
      const payment = payments.find(p => p.id === paymentId);
      if (!payment) {
        console.error('Payment not found');
        return;
      }
      
      // Check if payment method exists
      if (!payment.hasPaymentMethod) {
        showError('This influencer has not set up a payment method yet.', 'Please ask them to complete their payment information.', 5000);
        return;
      }

      // Prepare payment data for OTP
      const isNewPayment = paymentId.includes('_');
      let campaignId, influencerId;
      
      if (isNewPayment) {
        [campaignId, influencerId] = paymentId.split('_');
      } else {
        campaignId = payment.campaignId;
        influencerId = payment.influencerId;
      }

      // Send OTP
      const otpResult = await sendPaymentOTP({
        brandId: currentUser.uid,
        campaignId: campaignId,
        influencerId: influencerId,
        amount: payment.amount
      });

      if (otpResult.data.success) {
        setOtpData({
          otpId: otpResult.data.otpId,
          paymentId: paymentId,
          payment: payment,
          campaignId: campaignId,
          influencerId: influencerId
        });
        setTimeLeft(otpResult.data.expiresIn);
        setShowOTPModal(true);
        setOtp('');
        setOtpError('');
        
        // Close payment details modal if open
        if (showModal) {
          handleCloseModal();
        }
        
        showSuccess('Security code sent to your email. Please check your inbox.', 'A verification code has been sent to your registered email address.', 5000);
      } else {
        showError('Failed to send security code.', 'Please check your connection and try again.', 5000);
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      showError('There was an error sending the security code.', 'Please check your connection and try again.', 5000);
    } finally {
      setProcessingPayment(false);
    }
  };

  const sendBulkPaymentOTP = httpsCallable(functions, 'sendBulkPaymentOTP');

  const handleBulkProcessPayment = async () => {
    const selectedPayments = payments.filter(p => selectedPaymentIds.includes(p.id) && p.status === 'pending');
    
    if (selectedPayments.length === 0) {
      showError('No valid payments selected.', 'Please select pending payments to process.', 4000);
      return;
    }

    // Check all have payment methods
    const withoutMethod = selectedPayments.filter(p => !p.hasPaymentMethod);
    if (withoutMethod.length > 0) {
      showError(`${withoutMethod.length} influencer(s) have not set up a payment method.`, 'Please remove them from selection or ask them to set up payment info.', 5000);
      return;
    }

    try {
      setBulkProcessing(true);
      
      const totalAmount = selectedPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
      
      // Prepare payment items for bulk OTP
      const paymentItems = selectedPayments.map(p => {
        const isNewPayment = p.id.includes('_');
        let campaignId, influencerId;
        if (isNewPayment) {
          [campaignId, influencerId] = p.id.split('_');
        } else {
          campaignId = p.campaignId;
          influencerId = p.influencerId;
        }
        return {
          paymentId: p.id,
          campaignId,
          influencerId,
          amount: p.amount,
          influencerName: p.influencerName || '',
          campaignName: p.campaignTitle || ''
        };
      });

      const otpResult = await sendBulkPaymentOTP({
        brandId: currentUser.uid,
        payments: paymentItems,
        totalAmount
      });

      if (otpResult.data.success) {
        setBulkOtpData({
          otpId: otpResult.data.otpId,
          payments: paymentItems,
          totalAmount
        });
        setTimeLeft(otpResult.data.expiresIn);
        setShowBulkOTPModal(true);
        setOtp('');
        setOtpError('');
        
        showSuccess(`Security code sent for ${selectedPayments.length} payments totaling ${formatCurrency(totalAmount)}`, 'Please check your email for the verification code.', 5000);
      } else {
        showError('Failed to send security code.', 'Please try again.', 5000);
      }
    } catch (error) {
      console.error('Error sending bulk OTP:', error);
      showError('Failed to send security code.', 'Please check your connection and try again.', 5000);
    } finally {
      setBulkProcessing(false);
    }
  };

  const verifyBulkPaymentOTP = httpsCallable(functions, 'verifyBulkPaymentOTP');

  const handleBulkVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setOtpLoading(true);
      setOtpError('');

      const verifyResult = await verifyBulkPaymentOTP({
        otpId: bulkOtpData.otpId,
        otp: otp
      });

      if (verifyResult.data.success) {
        // Process each payment in sequence
        setBulkProgress({ current: 0, total: bulkOtpData.payments.length, completed: 0, failed: 0 });
        
        let completed = 0;
        let failed = 0;
        
        for (let i = 0; i < bulkOtpData.payments.length; i++) {
          const item = bulkOtpData.payments[i];
          setBulkProgress(prev => ({ ...prev, current: i + 1 }));
          
          try {
            const payment = payments.find(p => p.id === item.paymentId);
            if (payment) {
              await processActualPayment(item.paymentId, payment);
              completed++;
            }
          } catch (err) {
            console.error(`Error processing payment ${item.paymentId}:`, err);
            failed++;
          }
          
          setBulkProgress(prev => ({ ...prev, completed, failed }));
        }

        setShowBulkOTPModal(false);
        setBulkOtpData(null);
        setOtp('');
        setSelectedPaymentIds([]);
        
        if (failed === 0) {
          showSuccess(`All ${completed} payments processed successfully!`, 'Your payment requests have been submitted.', 6000);
        } else {
          showSuccess(`${completed} payments processed, ${failed} failed.`, 'Please retry the failed payments individually.', 8000);
        }
        
        fetchPayments();
      } else {
        setOtpError('Invalid security code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying bulk OTP:', error);
      if (error.code === 'functions/invalid-argument') {
        setOtpError('Invalid security code. Please try again.');
      } else if (error.code === 'functions/deadline-exceeded') {
        setOtpError('Security code has expired. Please request a new one.');
      } else {
        setOtpError('Verification failed. Please try again.');
      }
    } finally {
      setOtpLoading(false);
    }
  };
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit code');
      return;
    }

    try {
      setOtpLoading(true);
      setOtpError('');

      // Verify OTP
      const verifyResult = await verifyPaymentOTP({
        otpId: otpData.otpId,
        otp: otp
      });

      if (verifyResult.data.success) {
        // OTP verified, now process the actual payment
        await processActualPayment(otpData.paymentId, otpData.payment);
        
        setShowOTPModal(false);
        setOtpData(null);
        setOtp('');
        showSuccess('Payment request verified and sent to admin successfully!', 'Your payment request has been submitted and will be processed by our admin team.', 6000);
      } else {
        setOtpError('Invalid security code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      if (error.code === 'functions/invalid-argument') {
        setOtpError('Invalid security code. Please try again.');
      } else if (error.code === 'functions/deadline-exceeded') {
        setOtpError('Security code has expired. Please request a new one.');
      } else if (error.code === 'functions/resource-exhausted') {
        setOtpError('Too many failed attempts. Please request a new code.');
      } else {
        setOtpError('Verification failed. Please try again.');
      }
    } finally {
      setOtpLoading(false);
    }
  };

  // Process the actual payment after OTP verification
  const processActualPayment = async (paymentId, payment) => {
    try {
      
        // Get brand details
        const brandDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const brandData = brandDoc.exists() ? brandDoc.data() : {};
        const brandName = brandData.companyName || 
                         brandData.displayName || 
                         `${brandData.firstName || ''} ${brandData.lastName || ''}`.trim() || 
                         'Unknown Brand';
        
        // Create or update the payment record
        const isNewPayment = paymentId.includes('_'); // Check if this is a new payment (id format: campaignId_influencerId)
        
        if (isNewPayment) {
          // Create new payment record
          const [campaignId, influencerId] = paymentId.split('_');
          
          const paymentData = {
            campaignId,
            influencerId,
            brandId: currentUser.uid,
            brandName: brandName,
            influencerName: payment.influencerName || '',
            campaignName: payment.campaignTitle || '',
            amount: payment.amount,
            status: 'processing', // Brand has processed, waiting for admin
            paymentMethod: payment.paymentMethod ? payment.paymentMethod.type : 'unknown',
            paymentDetails: payment.paymentMethod,
            processedAt: Timestamp.now(),
            createdAt: Timestamp.now()
          };
          
          // Add to payments collection
          await addDoc(collection(db, 'payments'), paymentData);
          
          // Update the campaignParticipants record to mark as payment in progress
          if (payment.participantId) {
            const participantRef = doc(db, 'campaignParticipants', payment.participantId);
            await updateDoc(participantRef, {
              paymentStatus: 'processing'
            });
          }
        } else {
          // Update existing payment record
          const paymentRef = doc(db, 'payments', paymentId);
          await updateDoc(paymentRef, {
            status: 'processing',
            processedAt: Timestamp.now()
          });
        }
        
        // Refresh payments list
        fetchPayments();
        
      } catch (error) {
        console.error('Error processing payment:', error);
        throw error; // Re-throw to be handled by the calling function
      }
    };

  // Resend OTP
  const handleResendOTP = async () => {
    if (!otpData) return;
    
    try {
      setOtpLoading(true);
      setOtpError('');
      
      const otpResult = await sendPaymentOTP({
        brandId: currentUser.uid,
        campaignId: otpData.campaignId,
        influencerId: otpData.influencerId,
        amount: otpData.payment.amount
      });

      if (otpResult.data.success) {
        setOtpData(prev => ({
          ...prev,
          otpId: otpResult.data.otpId
        }));
        setTimeLeft(otpResult.data.expiresIn);
        setOtp('');
        showSuccess('New security code sent to your email.', 'A new verification code has been sent to your registered email address.', 3000);
      } else {
        setOtpError('Failed to resend security code. Please try again.');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      setOtpError('Failed to resend security code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Close OTP modal
  const handleCloseOTPModal = () => {
    setShowOTPModal(false);
    setOtpData(null);
    setOtp('');
    setOtpError('');
    setTimeLeft(0);
  };

  // Format time remaining
  const formatTimeLeft = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };
  
  // Get status badge with appropriate styling
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="mr-1 h-3 w-3" />
            Processing
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completed
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="mr-1 h-3 w-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };
  
  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    let date;
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };
  
  const fetchPayments = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);

      
      // Get all campaigns by this brand
      const campaignsRef = collection(db, 'campaigns');
      const campaignsQuery = query(
        campaignsRef,
        where('brandId', '==', currentUser.uid)
      );
      
      const campaignsSnapshot = await getDocs(campaignsQuery);
      const brandCampaigns = {};
      
      campaignsSnapshot.forEach((doc) => {
        const campaignData = doc.data();
        brandCampaigns[doc.id] = {
          id: doc.id,
          ...campaignData
        };
      });
      

      
      // Get campaign participants that have submitted content and are approved
      const participantsRef = collection(db, 'campaignParticipants');
      
      // We need to run two separate queries since Firestore doesn't support OR conditions
      // First query: Get participants with not_paid status
      const notPaidQuery = query(
        participantsRef,
        where('submissionStatus', '==', 'submitted'),
        where('status', '==', 'approved'),
        where('paymentStatus', '==', 'not_paid')
      );
      
      // Second query: Get participants with processing status
      const processingQuery = query(
        participantsRef,
        where('submissionStatus', '==', 'submitted'),
        where('status', '==', 'approved'),
        where('paymentStatus', '==', 'processing')
      );
      
      // Execute both queries
      const [notPaidSnapshot, processingSnapshot] = await Promise.all([
        getDocs(notPaidQuery),
        getDocs(processingQuery)
      ]);
      
      // Combine the results
      const participantDocs = [...notPaidSnapshot.docs, ...processingSnapshot.docs];

      
      // For each participant, check if they belong to one of our campaigns
      const paymentData = [];
      
      for (const participantDoc of participantDocs) {
        const participant = participantDoc.data();
        const campaignId = participant.campaignId;
        
        // Check if this is our campaign
        if (brandCampaigns[campaignId]) {

          
          // Get campaign details
          const campaign = brandCampaigns[campaignId];
          
          // Check if campaign has ended
          const campaignEndDate = campaign.endDate?.toDate ? campaign.endDate.toDate() : 
                                 campaign.endDate instanceof Date ? campaign.endDate :
                                 typeof campaign.endDate === 'string' ? new Date(campaign.endDate) : null;
          
          const now = new Date();
          const campaignHasEnded = campaignEndDate && campaignEndDate < now;
          
          // Skip this participant if campaign hasn't ended yet
          if (!campaignHasEnded) {
            continue;
          }
          
          // Get influencer details
          const influencerDoc = await getDoc(doc(db, 'users', participant.influencerId));
          const influencerData = influencerDoc.exists() ? influencerDoc.data() : {};
          
          // Get payment method if available
          const paymentMethodsRef = collection(db, 'paymentMethods');
          const paymentMethodsQuery = query(
            paymentMethodsRef,
            where('userId', '==', participant.influencerId),
            where('isDefault', '==', true)
          );
          
          const paymentMethodsSnapshot = await getDocs(paymentMethodsQuery);
          const paymentMethod = paymentMethodsSnapshot.size > 0 ? 
            paymentMethodsSnapshot.docs[0].data() : null;
          
          // Check if payment already exists
          const paymentsRef = collection(db, 'payments');
          const paymentsQuery = query(
            paymentsRef,
            where('campaignId', '==', campaignId),
            where('influencerId', '==', participant.influencerId)
          );
          
          const paymentsSnapshot = await getDocs(paymentsQuery);
          
          if (paymentsSnapshot.size === 0) {
            // No payment record exists yet, this is eligible for payment
            const amount = campaign.compensationAmount ? parseFloat(campaign.compensationAmount) : 0;
            
            paymentData.push({
              id: `${campaignId}_${participant.influencerId}`,
              campaignId: campaignId,
              campaignTitle: campaign.name || 'Campaign',
              campaignImage: campaign.coverImage,
              influencerId: participant.influencerId,
              influencerName: participant.influencerName || 
                influencerData.displayName || 
                `${influencerData.firstName || ''} ${influencerData.lastName || ''}`.trim() || 
                'Unknown Influencer',
              influencerEmail: influencerData.email || '',
              influencerAvatar: participant.influencerProfileUrl || influencerData.profileImageUrl || 'https://via.placeholder.com/40',
              amount: amount,
              status: 'pending',
              paymentMethod: paymentMethod,
              reviewId: participant.reviewId,
              hasPaymentMethod: !!paymentMethod,
              participantId: participantDoc.id
            });
          } else {
            // Payment record exists, add with its status
            const paymentDoc = paymentsSnapshot.docs[0];
            const amount = campaign.compensationAmount ? parseFloat(campaign.compensationAmount) : 0;
            
            paymentData.push({
              id: paymentDoc.id,
              ...paymentDoc.data(),
              campaignTitle: campaign.name || 'Campaign',
              campaignImage: campaign.coverImage,
              influencerName: participant.influencerName || 
                influencerData.displayName || 
                `${influencerData.firstName || ''} ${influencerData.lastName || ''}`.trim() || 
                'Unknown Influencer',
              influencerEmail: influencerData.email || '',
              influencerAvatar: participant.influencerProfileUrl || influencerData.profileImageUrl || 'https://via.placeholder.com/40',
              amount: amount, // Make sure amount is set correctly even for existing payments
              paymentMethod: paymentMethod,
              hasPaymentMethod: !!paymentMethod,
              participantId: participantDoc.id
            });
          }
        }
      }
      
      // Sort payments based on the selected sort option
      let sortedPayments = [...paymentData];
      if (sortBy === 'newest') {
        // For now we don't have a created date, so we'll leave as is
        // In a real app, we would sort by createdAt or similar field
      } else if (sortBy === 'amount') {
        sortedPayments.sort((a, b) => b.amount - a.amount);
      }
      

      setPayments(sortedPayments);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch ALL payments (completed, pending, processing, failed, rejected) for the transaction history
  const fetchAllPayments = async () => {
    if (!currentUser) return;

    try {
      setTransactionLoading(true);

      // Query the payments collection for all payments by this brand
      const paymentsRef = collection(db, 'payments');
      const paymentsQuery = query(
        paymentsRef,
        where('brandId', '==', currentUser.uid)
      );

      const paymentsSnapshot = await getDocs(paymentsQuery);

      // Build a map of campaign names for enrichment
      const campaignNames = {};
      const campaignIdsToFetch = new Set();
      paymentsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.campaignId) {
          campaignIdsToFetch.add(data.campaignId);
        }
      });

      // Fetch campaign names for any campaigns not already loaded
      for (const campaignId of campaignIdsToFetch) {
        if (!campaignNames[campaignId]) {
          try {
            const campaignDoc = await getDoc(doc(db, 'campaigns', campaignId));
            if (campaignDoc.exists()) {
              campaignNames[campaignId] = campaignDoc.data().name || 'Untitled Campaign';
            } else {
              campaignNames[campaignId] = 'Untitled Campaign';
            }
          } catch (err) {
            campaignNames[campaignId] = 'Untitled Campaign';
          }
        }
      }

      const allPaymentData = [];
      paymentsSnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        allPaymentData.push({
          id: docSnap.id,
          ...data,
          campaignName: data.campaignName || campaignNames[data.campaignId] || 'Untitled Campaign',
          influencerName: data.influencerName || 'Unknown Influencer',
          amount: data.amount || 0,
          status: data.status || 'pending',
          paymentMethod: data.paymentMethod,
          paymentMethodType: data.paymentMethod && typeof data.paymentMethod === 'object'
            ? data.paymentMethod.type
            : data.paymentMethod,
          createdAt: data.createdAt || data.processedAt || data.completedAt,
          completedAt: data.completedAt,
          processedAt: data.processedAt
        });
      });

      // Sort by date descending (newest first)
      allPaymentData.sort((a, b) => {
        const dateA = a.createdAt instanceof Timestamp ? a.createdAt.toDate() :
                      a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) :
                      a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt instanceof Timestamp ? b.createdAt.toDate() :
                      b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) :
                      b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });

      setAllPayments(allPaymentData);
    } catch (error) {
      console.error('Error fetching all payments:', error);
    } finally {
      setTransactionLoading(false);
    }
  };

  // Get filtered transactions based on status, search, and date range
  const getFilteredTransactions = () => {
    let filtered = [...allPayments];

    // Status filter
    if (transactionFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === transactionFilter);
    }

    // Search by influencer name
    if (transactionSearch.trim()) {
      const searchLower = transactionSearch.toLowerCase().trim();
      filtered = filtered.filter(payment =>
        (payment.influencerName || '').toLowerCase().includes(searchLower)
      );
    }

    // Date range filter
    if (transactionDateRange !== 'all') {
      const now = new Date();
      let cutoffDate = null;
      if (transactionDateRange === '7days') {
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (transactionDateRange === '30days') {
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      } else if (transactionDateRange === '90days') {
        cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      }

      if (cutoffDate) {
        filtered = filtered.filter(payment => {
          const paymentDate = payment.createdAt instanceof Timestamp ? payment.createdAt.toDate() :
                              payment.createdAt?.seconds ? new Date(payment.createdAt.seconds * 1000) :
                              payment.createdAt ? new Date(payment.createdAt) : null;
          return paymentDate && paymentDate >= cutoffDate;
        });
      }
    }

    return filtered;
  };

  // Get transaction summary stats
  const getTransactionSummary = () => {
    const completed = allPayments.filter(p => p.status === 'completed');
    const pending = allPayments.filter(p => p.status === 'pending');
    const failed = allPayments.filter(p => p.status === 'failed' || p.status === 'rejected');

    return {
      totalPaid: completed.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      totalPending: pending.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      totalFailed: failed.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
      totalCount: allPayments.length
    };
  };

  // Export filtered transactions to CSV
  const exportToCSV = () => {
    const filtered = getFilteredTransactions();

    if (filtered.length === 0) {
      showError('No transactions to export.', 'Adjust your filters and try again.', 3000);
      return;
    }

    const headers = ['Date', 'Influencer', 'Campaign', 'Amount', 'Method', 'Status'];

    const rows = filtered.map(payment => {
      const date = payment.createdAt instanceof Timestamp ? payment.createdAt.toDate() :
                   payment.createdAt?.seconds ? new Date(payment.createdAt.seconds * 1000) :
                   payment.createdAt ? new Date(payment.createdAt) : null;
      const dateStr = date ? date.toLocaleDateString('en-US') : 'N/A';
      const influencer = payment.influencerName || 'Unknown';
      const campaign = payment.campaignName || 'Untitled Campaign';
      const amount = payment.amount || 0;
      const method = payment.paymentMethodType || (payment.paymentMethod && payment.paymentMethod.type) || 'unknown';
      const status = payment.status || 'unknown';

      // Escape CSV values (wrap in quotes, escape internal quotes)
      const escapeCSV = (val) => `"${String(val).replace(/"/g, '""')}"`;

      return [dateStr, influencer, campaign, amount, method, status].map(escapeCSV).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');

    // Create Blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'payment-history.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    showSuccess('CSV exported successfully.', `${filtered.length} transactions exported to payment-history.csv`, 3000);
  };

  // Format payment method type for display
  const formatPaymentMethod = (payment) => {
    if (!payment.paymentMethod && !payment.paymentMethodType) return 'Unknown';
    const type = payment.paymentMethodType || (payment.paymentMethod && payment.paymentMethod.type);
    if (!type) return 'Unknown';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Get transaction date (prefers completed date, then processed, then created)
  const getTransactionDate = (payment) => {
    const dateSource = payment.completedAt || payment.processedAt || payment.createdAt;
    return dateSource ? formatDate(dateSource) : 'N/A';
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Influencer Payments</h2>
        <p className="text-gray-600">Process payments for influencers who have completed campaigns. Once processed, payments will be sent to the admin for final payout.</p>
      </div>
      
      <div className="bg-white shadow-sm rounded-lg">
        
        <div className="p-6">
          {/* Filter and Sort Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${filter === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('pending')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${filter === 'pending' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Pending
              </button>
              <button
                onClick={() => setFilter('processing')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${filter === 'processing' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Processing
              </button>
              <button
                onClick={() => setFilter('completed')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${filter === 'completed' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                Completed
              </button>
            </div>

            <div className="relative">
              <Select
                value={sortBy}
                onValueChange={(value) => setSortBy(value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="amount">Amount (High to Low)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk Action Bar */}
          {selectedPaymentIds.length > 0 && (
            <div className="sticky top-0 z-10 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-blue-900">
                  {selectedPaymentIds.length} payment{selectedPaymentIds.length !== 1 ? 's' : ''} selected
                </span>
                <span className="ml-3 text-sm text-blue-700">
                  Total: {formatCurrency(selectedPaymentIds.reduce((sum, id) => {
                    const p = payments.find(p => p.id === id);
                    return sum + (p ? (parseFloat(p.amount) || 0) : 0);
                  }, 0))}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedPaymentIds([])}
                  className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1"
                >
                  Clear
                </button>
                <button
                  onClick={handleBulkProcessPayment}
                  disabled={bulkProcessing}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {bulkProcessing ? 'Processing...' : `Pay Selected (${selectedPaymentIds.length})`}
                </button>
              </div>
            </div>
          )}

          {/* Payments List */}
          {loading ? (
            null
          ) : getFilteredPayments().length > 0 ? (
            <div className="overflow-hidden">
              {filter === 'pending' && getFilteredPayments().filter(p => p.status === 'pending').length > 0 && (
                <div className="flex items-center justify-between pb-3 mb-2 border-b border-gray-200">
                  <button
                    onClick={() => {
                      const pendingIds = getFilteredPayments().filter(p => p.status === 'pending').map(p => p.id);
                      setSelectedPaymentIds(
                        pendingIds.every(id => selectedPaymentIds.includes(id)) 
                          ? selectedPaymentIds.filter(id => !pendingIds.includes(id))
                          : [...new Set([...selectedPaymentIds, ...pendingIds])]
                      );
                    }}
                    className="inline-flex items-center text-sm text-gray-600 hover:text-primary transition-colors"
                  >
                    {getFilteredPayments().filter(p => p.status === 'pending').every(p => selectedPaymentIds.includes(p.id))
                      ? <CheckSquare className="h-4 w-4 mr-2 text-primary" />
                      : <Square className="h-4 w-4 mr-2" />}
                    Select All Pending
                  </button>
                  <span className="text-sm text-gray-500">
                    {selectedPaymentIds.filter(id => getFilteredPayments().find(p => p.id === id && p.status === 'pending')).length} selected
                  </span>
                </div>
              )}
              <ul className="divide-y divide-gray-200">
                {getFilteredPayments().map((payment) => (
                  <li key={payment.id} className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center">
                      {filter === 'pending' && payment.status === 'pending' && (
                        <div className="flex-shrink-0 mr-3">
                          <button
                            onClick={() => {
                              setSelectedPaymentIds(prev => 
                                prev.includes(payment.id) 
                                  ? prev.filter(id => id !== payment.id)
                                  : [...prev, payment.id]
                              );
                            }}
                            className="text-gray-400 hover:text-primary transition-colors"
                          >
                            {selectedPaymentIds.includes(payment.id) 
                              ? <CheckSquare className="h-5 w-5 text-primary" />
                              : <Square className="h-5 w-5" />}
                          </button>
                        </div>
                      )}
                      {/* Campaign image removed as requested */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center mb-1">
                          <img
                            src={payment.influencerAvatar}
                            alt={payment.influencerName}
                            className="h-5 w-5 rounded-full mr-1"
                          />
                          <p className="text-sm font-medium text-gray-900">
                            {payment.influencerName}
                          </p>
                        </div>
                        <p className="text-sm text-gray-500 mb-1 truncate">
                          Campaign: {payment.campaignTitle || 'Untitled Campaign'}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {getStatusBadge(payment.status)}
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <DollarSign className="mr-1 h-3 w-3" />
                            {formatCurrency(payment.amount)}
                          </span>
                          {payment.processedAt && (
                            <span className="text-xs text-gray-500">
                              Processed: {formatDate(payment.processedAt)}
                            </span>
                          )}
                          {payment.completedAt && (
                            <span className="text-xs text-gray-500">
                              Completed: {formatDate(payment.completedAt)}
                            </span>
                          )}
                        </div>
                        {payment.paymentMethod ? (
                          <p className="text-sm text-gray-600">
                            Payment Method: <span className="font-medium">{payment.paymentMethod.type}</span>
                            {payment.paymentMethod.type === 'bank' && (
                              <span> ({payment.paymentMethod.details?.bankName || payment.paymentMethod.bankName})</span>
                            )}
                          </p>
                        ) : (
                          <p className="text-sm text-red-500">
                            No payment method set by influencer
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 mt-4 md:mt-0 md:ml-4">
                        {payment.status === 'pending' ? (
                          <button
                            onClick={() => handleProcessPayment(payment.id)}
                            disabled={!payment.hasPaymentMethod || processingPayment}
                            className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-sm font-medium rounded-md ${!payment.hasPaymentMethod ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'border-green-500 text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'}`}
                            title={!payment.hasPaymentMethod ? 'Influencer has not set up a payment method' : 'Send payment request to admin'}
                          >
                            Send to Admin
                          </button>
                        ) : (
                          <button
                            onClick={() => handleViewPayment(payment)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                          >
                            View Details
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500">No payments found matching your filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* Transaction History Section */}
      <div className="mt-8">
        <div className="mb-4">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Transaction History</h2>
          <p className="text-gray-600">A complete ledger of all your payment transactions, including pending, processing, completed, and failed payments.</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-green-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Paid</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(getTransactionSummary().totalPaid)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-yellow-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <Clock className="h-5 w-5 text-yellow-600" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Pending</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(getTransactionSummary().totalPending)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-red-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Failed</p>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(getTransactionSummary().totalFailed)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-blue-500">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Transactions</p>
                <p className="text-lg font-semibold text-gray-900">{getTransactionSummary().totalCount}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Table Card */}
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          {/* Filter Controls */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
              {/* Status Filter Buttons */}
              <div className="flex flex-wrap space-x-2">
                <button
                  onClick={() => setTransactionFilter('all')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${transactionFilter === 'all' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  All
                </button>
                <button
                  onClick={() => setTransactionFilter('pending')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${transactionFilter === 'pending' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Pending
                </button>
                <button
                  onClick={() => setTransactionFilter('processing')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${transactionFilter === 'processing' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Processing
                </button>
                <button
                  onClick={() => setTransactionFilter('completed')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${transactionFilter === 'completed' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Completed
                </button>
                <button
                  onClick={() => setTransactionFilter('failed')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${transactionFilter === 'failed' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Failed
                </button>
              </div>

              {/* Search and Date Range and Export */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
                {/* Search by influencer name */}
                <div className="relative w-full sm:w-64">
                  <input
                    type="text"
                    value={transactionSearch}
                    onChange={(e) => setTransactionSearch(e.target.value)}
                    placeholder="Search influencer name..."
                    className="w-full pl-3 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Date Range Filter */}
                <Select
                  value={transactionDateRange}
                  onValueChange={(value) => setTransactionDateRange(value)}
                >
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Date range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    <SelectItem value="7days">Last 7 Days</SelectItem>
                    <SelectItem value="30days">Last 30 Days</SelectItem>
                    <SelectItem value="90days">Last 90 Days</SelectItem>
                  </SelectContent>
                </Select>

                {/* Export CSV Button */}
                <button
                  onClick={exportToCSV}
                  disabled={allPayments.length === 0}
                  className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-sm font-medium rounded-md ${allPayments.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'border-blue-500 text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Transaction Table */}
          {transactionLoading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <p className="text-gray-500 mt-2 text-sm">Loading transactions...</p>
            </div>
          ) : getFilteredTransactions().length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Influencer
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Method
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getFilteredTransactions().map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {payment.influencerName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {payment.campaignName || 'Untitled Campaign'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(payment.amount)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 capitalize">
                          {formatPaymentMethod(payment)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {getTransactionDate(payment)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewPayment(payment)}
                          className="text-primary hover:text-primary-dark"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-500">No transactions found matching your filters.</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Information */}
      <div className="mt-8 bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">Payment Process Information</h3>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-2">How Payments Work</h4>
            <p className="text-sm text-gray-600">
              After an influencer completes a campaign and their content is approved, you can send a payment request to the admin. The admin will then process the actual payout to the influencer. The payment process follows these steps:
            </p>
            <ol className="mt-2 list-decimal list-inside text-sm text-gray-600 space-y-1">
              <li>Influencer submits content for your campaign</li>
              <li>You approve the content (via Content Approval)</li>
              <li>You send the payment request to admin using this page</li>
              <li>Admin receives the request and processes the payout to the influencer</li>
              <li>Payment status is updated to 'completed' once the influencer receives payment</li>
            </ol>
          </div>
          
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-2">Payment Methods</h4>
            <p className="text-sm text-gray-600 mb-4">
              Influencers can set up their preferred payment methods in their profiles. You'll be able to see their payment method when sending payment requests to the admin. The admin will use this information to process the payout.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">PP</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">PayPal</h5>
                  <p className="text-xs text-gray-500">1-2 business days</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">BT</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">Bank Transfer</h5>
                  <p className="text-xs text-gray-500">3-5 business days</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-md p-4 flex items-center">
                <div className="h-10 w-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-purple-600 font-bold">GC</span>
                </div>
                <div>
                  <h5 className="text-sm font-medium text-gray-900">GCash</h5>
                  <p className="text-xs text-gray-500">1-2 business days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Detail Modal */}
      {showModal && selectedPayment && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Payment Details
                    </h3>
                    <div className="mt-4">
                      <div className="flex items-center mb-3">
                        <img
                          src={selectedPayment.influencerAvatar}
                          alt={selectedPayment.influencerName}
                          className="h-10 w-10 rounded-full mr-3"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{selectedPayment.influencerName}</p>
                          <p className="text-xs text-gray-500">{selectedPayment.influencerEmail}</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 py-3">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Campaign</dt>
                            <dd className="mt-1 text-sm text-gray-900">{selectedPayment.campaignTitle}</dd>
                          </div>
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Amount</dt>
                            <dd className="mt-1 text-sm text-gray-900">{formatCurrency(selectedPayment.amount)}</dd>
                          </div>
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Status</dt>
                            <dd className="mt-1 text-sm text-gray-900">{getStatusBadge(selectedPayment.status)}</dd>
                          </div>
                          <div className="sm:col-span-1">
                            <dt className="text-sm font-medium text-gray-500">Processed Date</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {selectedPayment.processedAt ? formatDate(selectedPayment.processedAt) : 'Not processed yet'}
                            </dd>
                          </div>
                          <div className="sm:col-span-2">
                            <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {selectedPayment.paymentMethod ? (
                                <div className="border border-gray-200 rounded-md p-3">
                                  <p className="font-medium text-gray-800 capitalize mb-2">{selectedPayment.paymentMethod.type} Transfer</p>
                                  {selectedPayment.paymentMethod.type === 'bank' && (
                                    <div className="space-y-1">
                                      <div className="grid grid-cols-3">
                                        <p className="text-sm text-gray-500">Bank:</p>
                                        <p className="text-sm font-medium text-gray-800 col-span-2">{selectedPayment.paymentMethod.details?.bankName || selectedPayment.paymentMethod.bankName}</p>
                                      </div>
                                      <div className="grid grid-cols-3">
                                        <p className="text-sm text-gray-500">Account Name:</p>
                                        <p className="text-sm font-medium text-gray-800 col-span-2">{selectedPayment.paymentMethod.details?.accountName || selectedPayment.paymentMethod.accountName}</p>
                                      </div>
                                      <div className="grid grid-cols-3">
                                        <p className="text-sm text-gray-500">Account Number:</p>
                                        <p className="text-sm font-medium text-gray-800 col-span-2">{selectedPayment.paymentMethod.details?.accountNumber || selectedPayment.paymentMethod.accountNumber}</p>
                                      </div>
                                    </div>
                                  )}
                                  {selectedPayment.paymentMethod.type === 'paypal' && (
                                    <p className="text-sm">Email: {selectedPayment.paymentMethod.email}</p>
                                  )}
                                  {selectedPayment.paymentMethod.type === 'gcash' && (
                                    <p className="text-sm">Number: {selectedPayment.paymentMethod.phoneNumber}</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-red-500">No payment method set by influencer</p>
                              )}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                {selectedPayment.status === 'pending' && (
                  <button
                    type="button"
                    onClick={() => handleProcessPayment(selectedPayment.id)}
                    disabled={!selectedPayment.hasPaymentMethod || processingPayment}
                    className={`w-full inline-flex justify-center rounded-md border shadow-sm px-4 py-2 text-base font-medium sm:ml-3 sm:w-auto sm:text-sm ${!selectedPayment.hasPaymentMethod ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'border-green-500 text-green-700 bg-white hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'}`}
                  >
                    {processingPayment ? 'Processing...' : 'Send to Admin'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOTPModal && otpData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">Security Verification</h3>
                    <p className="text-sm text-gray-500">Payment authorization required</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseOTPModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  We've sent a 6-digit security code to your email address.
                </p>
                <div className="text-xs text-blue-600">
                  <strong>Payment Details:</strong><br />
                  Campaign: {otpData.payment.campaignTitle}<br />
                  Influencer: {otpData.payment.influencerName}<br />
                  Amount: ₱{otpData.payment.amount?.toLocaleString()}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Security Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                    if (otpError) setOtpError('');
                  }}
                  placeholder="000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  maxLength={6}
                  autoComplete="off"
                />
                {otpError && (
                  <p className="mt-1 text-sm text-red-600">{otpError}</p>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between text-sm">
                <div className="text-gray-500">
                  {timeLeft > 0 ? (
                    <span>Code expires in: <strong className="text-red-600">{formatTimeLeft(timeLeft)}</strong></span>
                  ) : (
                    <span className="text-red-600">Code expired</span>
                  )}
                </div>
                <button
                  onClick={handleResendOTP}
                  disabled={otpLoading || timeLeft > 240} // Allow resend only in last minute or after expiry
                  className="text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {otpLoading ? 'Sending...' : 'Resend Code'}
                </button>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleCloseOTPModal}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleVerifyOTP}
                  disabled={otpLoading || otp.length !== 6 || timeLeft === 0}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {otpLoading ? 'Verifying...' : 'Verify & Send'}
                </button>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <ShieldCheck className="h-5 w-5 text-yellow-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Security Notice:</strong> Never share this code with anyone. 
                      This verification ensures only you can authorize payments.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk OTP Modal */}
      {showBulkOTPModal && bulkOtpData && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => !otpLoading && setShowBulkOTPModal(false)}></div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Authorize Bulk Payment
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        You are about to process <strong>{bulkOtpData.payments.length} payments</strong> totaling <strong>{formatCurrency(bulkOtpData.totalAmount)}</strong>.
                      </p>
                      
                      {/* Payment summary */}
                      <div className="mt-3 max-h-32 overflow-y-auto bg-gray-50 rounded-md p-3">
                        {bulkOtpData.payments.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-xs py-1">
                            <span className="text-gray-700">{item.influencerName}</span>
                            <span className="font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Progress (shown during processing) */}
                      {bulkProgress.total > 0 && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-gray-600 mb-1">
                            <span>Processing payments... {bulkProgress.current}/{bulkProgress.total}</span>
                            <span>{bulkProgress.completed} done, {bulkProgress.failed} failed</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}></div>
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-3 bg-amber-50 border border-amber-200 rounded-md p-3">
                        <p className="text-sm text-amber-800">Enter the 6-digit code sent to your email:</p>
                        <input
                          type="text"
                          maxLength="6"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                          disabled={otpLoading || bulkProgress.total > 0}
                          className="mt-2 block w-full text-center text-2xl tracking-widest border border-gray-300 rounded-md py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                          placeholder="------"
                        />
                        {timeLeft > 0 && (
                          <p className="text-xs text-amber-600 mt-1">Code expires in {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</p>
                        )}
                        {otpError && <p className="text-xs text-red-600 mt-1">{otpError}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  onClick={handleBulkVerifyOTP}
                  disabled={otpLoading || otp.length !== 6 || bulkProgress.total > 0}
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {otpLoading ? 'Verifying...' : bulkProgress.total > 0 ? 'Processing...' : 'Verify & Pay All'}
                </button>
                <button
                  onClick={() => { if (!otpLoading) { setShowBulkOTPModal(false); setBulkOtpData(null); setOtp(''); setOtpError(''); setBulkProgress({ current: 0, total: 0, completed: 0, failed: 0 }); } }}
                  disabled={otpLoading || bulkProgress.total > 0}
                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
